import JobModel from '@/common/models/jobs/job-model';
import BackendJobModel from '@/common/models/jobs/backend-job-model';
import {DataExportModel} from '@/common/models/data-export-model';
import timezone from '@/common/timezone';

export default class DataExportJobModel extends BackendJobModel {
    hydrate(fields = {}) {
        super.hydrate(fields);
        const {
            downloadLink = null,
            name = null,
            processedOn = null,
            scheduledTime = null,
        } = fields;
        this.type = JobModel.Types.dataExport;
        this.icon = 'data-exports';
        this.name = name;
        this.scheduledTime = timezone.readUTC(scheduledTime);
        if (this.isCompleted) {
            // support hydration from either a model or API response
            this.downloadLink = downloadLink
                || (fields.$links ? fields.$links.signedLink.href : null)
                || (DataExportModel.getSignedLink(fields._links));
            this.processedOn = timezone.readUTC(processedOn || fields._embedded.file.createdTime);
        }
    }

    get jobTime() {
        return this.processedOn || this.scheduledTime;
    }

    // Data export is completed
    get isCompleted() {
        return this.status === DataExportModel.Statuses.completed;
    }

    // Data export is processing or about to be processed
    get isActive() {
        return [DataExportModel.Statuses.queued,
            DataExportModel.Statuses.processing]
            .includes(this.status);
    }

    // Data export is not due to be processed soon or at all
    get isInactive() {
        return [DataExportModel.Statuses.completed,
            DataExportModel.Statuses.expired,
            DataExportModel.Statuses.failed,
            DataExportModel.Statuses.purged]
            .includes(this.status);
    }

    // Summary of the job for display in the alerts-menu
    get label() {
        return this.name;
    }

    // Toast message to be displayed on completion of the job
    get toastMessage() {
        return {
            type: 'positive',
            text: `Data Export ${this.name} is ready to download.`,
        };
    }

    static async pollJobs(api, rootGetters) {
        const {
            items: dataExportsItems,
        } = await api(({dataExports}) => dataExports.allowedOnly.getAll({
            limit: 10,
            expand: 'file',
            filter: `userId:${rootGetters['user/userId']}`,
            sort: '-scheduledTime',
        }));

        const jobs = {running: [], completed: []};

        dataExportsItems.forEach((item) => {
            const {fields} = item;
            const job = new DataExportJobModel(fields);
            if (job.isActive) {
                jobs.running.push(job);
            } else if (job.isInactive) {
                jobs.completed.push(job);
            }
        });
        return jobs;
    }

    getPayload() {
        return {
            ...super.getPayload(),
            type: this.type,
            icon: this.icon,
            name: this.name,
            downloadLink: this.downloadLink,
            processedOn: this.processedOn,
            scheduledTime: this.scheduledTime,
        };
    }
}
